import { Link, useNavigate } from 'react-router-dom';
import GiftsShopModal from '../../components/gifts/GiftsShopModal';
import React, { useEffect, useState } from 'react';
import Policy from '../../components/shared/Policy';
import config from '../../config';
import { useDispatch, useSelector } from 'react-redux';
import useAxiosPrivate from '../../hooks/useAxiosPrivate';
import DotSpinner from '../../components/shared/DotSpinner';
import { useSubscription } from '../../hooks/useSubscription';
import { calculateAge } from '../../utils/calculateAge';
import { BottomModal } from '../../components/shared/BottomModal';
import { ToggleSwitch } from '../../components/shared/ToggleSwitch';
import { ProfileAcceptanceLinks } from './ProfileAcceptanceLinks';
import { openTelegramLink } from '../../utils/openLink';
import { toast } from 'react-toastify';
import { userApi, useGetUserQuery } from '../../api/userApi';
import { apiTrials } from '../../api/apiTrials.api';

// Триал для плашки «Попробуй PREMIUM бесплатно» (3 дня, один раз на аккаунт).
// Создаётся сидом: node src/scripts/seed-trial-menu.js — id из вывода зашить сюда
const MENU_TRIAL_CODE = '6ae8e33dd58d81d3d217f063';

const MENU_ITEMS = [
  {
    label: 'Мой профиль',
    icon: '/icons/profile_edit.svg',
    action: (ctx) => ctx.navigate('/ProfileEdit')
  },
  // {
  //   label: 'Магазин подарков',
  //   icon: '/icons/profile_presents_shop.svg',
  //   action: (ctx) => ctx.setPresentsShop(true)
  // },
  {
    label: 'Пригласить друга',
    icon: '/icons/profile_share.svg',
    action: (ctx) => ctx.navigate('/invite')
  },
  {
    label: 'Техническая поддержка',
    icon: '/icons/support.svg',
    action: () => openTelegramLink('https://t.me/myta_support')
  },
  {
    label: 'Управление подпиской',
    icon: '/icons/sub-icon.svg',
    condition: (ctx) => ctx.isActiveSubscription && !ctx.isSubscriptionLoading,
    action: (ctx) => ctx.navigate(ctx.routes.SUBSCRIPTION.MAIN)
  }
];

function ProfileMenu() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isActiveSubscription, isLoading: isSubscriptionLoading } = useSubscription();
  const { userId } = useSelector((state) => state.auth);
  const axiosPrivate = useAxiosPrivate();

  // Кэш RTK Query: при повторном входе данные показываются сразу из кэша,
  // а refetchOnMountOrArgChange догружает свежие (баланс монет и т.д.) в фоне.
  // Спиннер остаётся только на самый первый заход, пока кэша ещё нет.
  const {
    data: userData,
    isLoading,
    isError,
    refetch
  } = useGetUserQuery(userId, {
    skip: !userId,
    refetchOnMountOrArgChange: true
  });

  const [presentsShop, setPresentsShop] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);
  const [isTogglingHidden, setIsTogglingHidden] = useState(false);

  // Доступен ли бесплатный триал из меню: проверяем на сервере —
  // после активации он помечается использованным и плашка исчезает навсегда
  const [isTrialAvailable, setIsTrialAvailable] = useState(false);

  useEffect(() => {
    if (isActiveSubscription || isSubscriptionLoading) return;

    let cancelled = false;
    apiTrials
      .checkAvailability(axiosPrivate, MENU_TRIAL_CODE)
      .then((res) => {
        if (!cancelled) setIsTrialAvailable(Boolean(res?.available));
      })
      .catch(() => {
        // «уже использован» / триал не найден — плашки не будет
        if (!cancelled) setIsTrialAvailable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isActiveSubscription, isSubscriptionLoading, axiosPrivate]);

  // Скрытие анкеты из показа при свайпах
  const handleToggleHidden = async (hidden) => {
    const prev = userData.isProfileHidden;
    // Оптимистичное обновление кэша RTK Query
    dispatch(
      userApi.util.updateQueryData('getUser', userId, (draft) => {
        draft.isProfileHidden = hidden;
      })
    );
    setIsTogglingHidden(true);

    try {
      await axiosPrivate.put(config.API.USERS.HIDE_PROFILE, { hidden });
      toast.success(hidden ? 'Анкета скрыта — вас не видно при свайпах' : 'Анкета снова видна при свайпах');
    } catch (error) {
      // Откат кэша при ошибке
      dispatch(
        userApi.util.updateQueryData('getUser', userId, (draft) => {
          draft.isProfileHidden = prev;
        })
      );
      toast.error('Не удалось изменить видимость анкеты');
    } finally {
      setIsTogglingHidden(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[100vh] bg-[#010D0D]">
        <DotSpinner />
      </div>
    );
  }

  if (isError || !userData) {
    return <div className="flex items-center justify-center h-screen text-white">Error loading user data</div>;
  }

  return (
    <div className="flex flex-1 flex-col px-4 pb-[26px] gap-6 bg-[#010D0D] overflow-y-scroll">
      <div className="flex-1 overflow-y-auto pt-4 ">
        <div className="flex flex-row text-white w-full">
          <img
            alt="Аватар"
            src={userData.photos?.[0]}
            className="w-[64px] h-[64px] mr-[13px] rounded-full object-cover shrink-0"
          />
          <div className="flex flex-col flex-1 min-w-0 font-raleway">
            <div className="flex flex-wrap w-full min-w-0 gap-1">
              <p className="text-[18px] font-medium truncate">{userData.name},</p>
              <p className="text-[18px] font-medium">{calculateAge(userData.birthDay)} лет</p>
            </div>
            <div className="flex items-center justify-start mt-[2px] gap-1">
              <p className="opacity-50 text-lg">Баланс:</p>
              <div className="flex flex-1 text-[20px] items-center">
                <img alt="Монета" src="/icons/coin.svg" className="mr-[4px] w-[20px] h-[20px]" />{' '}
                {userData.wallet?.coins.toFixed(2) || 0}
              </div>
            </div>
          </div>
        </div>

        <div
          className="mb-6 bg-[#A1F69E1A] h-[44px] rounded-[400px] w-full text-[#A1F69E] text-[18px] flex flex-row items-center justify-center mt-5 gap-2"
          onClick={() => navigate('/makepayment')}
        >
          <img alt="Монета" src="/icons/coin.svg" className="ml-2 w-[20px] h-[20px]" />
          Купить монеты
        </div>

        {!isActiveSubscription && !isSubscriptionLoading && (
          <div
            className="mb-4 flex flex-row items-center justify-between w-full p-4 rounded-[16px] bg-[#1A2727] border border-[#A1F69E] cursor-pointer font-raleway"
            onClick={() => navigate('/premium')}
          >
            <div>
              <p className=" font-bold text-[#A1F69E] text-[15px] flex-1">Включи PREMIUM</p>
              <p className="text-[#FDFDFDCC]/80 text-[14px] font-medium">
                C Premium вы найдёте свою любовь в разы быстрее
              </p>
            </div>
            <ToggleSwitch checked={Boolean(isActiveSubscription)} />
          </div>
        )}

        {/* 🔥 Бесплатный триал на 3 дня: один раз на аккаунт, только без подписки */}
        {!isActiveSubscription && !isSubscriptionLoading && isTrialAvailable && (
          <div
            className="mb-4 bg-[#1A2727] border border-[#A1F69E] p-3 rounded-[12px] w-full text-[#A1F69E] text-[16px] font-medium flex flex-row items-center justify-between gap-2 cursor-pointer font-raleway"
            onClick={() => navigate(config.ROUTES.PREMIUM_TRIAL(MENU_TRIAL_CODE))}
          >
            <p>
              <span className="text-[18px] leading-none">🔥 </span>
              Попробуй PREMIUM бесплатно
            </p>
            <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M-7.53403e-05 5.4082H12.3076M7.30762 0.408203L12.3076 5.4082L7.30762 10.4082"
                stroke="#A1F69E"
                stroke-width="1.15385"
              />
            </svg>
          </div>
        )}

        <div className="mb-6 flex flex-row items-center justify-between w-full p-4 rounded-[16px] bg-[#1A2727] cursor-pointer font-raleway">
          <div>
            <p className=" font-bold text-[#FDFDFD] text-[15px] flex-1">Скрыть профиль</p>
            <p className="text-[#FDFDFDCC]/80 text-[14px] font-medium">
              Ты останешься видимым для тех, кто уже поставил тебе лайк
            </p>
          </div>
          <ToggleSwitch
            checked={Boolean(userData.isProfileHidden)}
            disabled={isTogglingHidden}
            onChange={handleToggleHidden}
          />
        </div>

        <div className="flex flex-col justify-start w-full text-white gap-[15px]">
          {MENU_ITEMS.map((item, index) => {
            const isVisible = item.condition ? item.condition({ isActiveSubscription, isSubscriptionLoading }) : true;

            if (!isVisible) return null;

            return (
              <div
                key={index}
                className="flex flex-row gap-1 items-center w-full cursor-pointer"
                onClick={() =>
                  item.action({
                    navigate,
                    setPresentsShop,
                    routes: config.ROUTES
                  })
                }
              >
                <img alt={item.label} src={item.icon} className="w-[32px] h-[32px]" />
                {item.label}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex mt-auto flex-col gap-2 items-start ">
        <ProfileAcceptanceLinks />
        <div className="flex flex-col border-t border-[#ffffff52] py-2 w-full">
          <Link to={config.ROUTES.PROFILE.DELETE} className="text-white text-sm font-medium">
            Удалить аккаунт
          </Link>
        </div>
      </div>

      {presentsShop && <GiftsShopModal onRefetchFn={refetch} onClose={() => setPresentsShop(false)} />}

      <BottomModal isOpen={showPolicy} onClose={() => setShowPolicy(false)}>
        <>
          <h3 className="text-white text-xl font-raleway font-semibold px-4">Политика конфиденциальности</h3>
          <Policy />
        </>
      </BottomModal>
    </div>
  );
}

export default ProfileMenu;
